/**
 * QuickReturnsBar — single-row, at-a-glance numerical returns strip
 * (1D · 5D · 10D · 20D · 50D · 100D · 200D · 1Y · 3Y · 5Y) for the
 * currently-loaded ticker on the Stock Analysis page.  Mirrors the
 * green/red color language of the GICS Sector & Sub-Sector table.
 *
 * Yahoo occasionally rate-limits or times out on the underlying
 * multi-window fetch — when that happens we render a subtle error
 * pill with a Refresh button so the user can retry without reloading
 * the whole page.  An auto-retry (once, after 4 s) covers transient
 * blips silently.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { wmCellExtreme, wmCurrentBadge } from "./wmExtremes";
import { API } from "../config/api";
import { useLiveQuote, LiveSourceLabel } from "../lib/liveQuote";

const WINDOWS = [
  { key: "ret_1d",   dd: "dd_1d",   rec: "rec_1d",   label: "1D"   },
  { key: "ret_5d",   dd: "dd_5d",   rec: "rec_5d",   label: "5D"   },
  { key: "ret_10d",  dd: "dd_10d",  rec: "rec_10d",  label: "10D"  },
  { key: "ret_20d",  dd: "dd_20d",  rec: "rec_20d",  label: "20D"  },
  { key: "ret_50d",  dd: "dd_50d",  rec: "rec_50d",  label: "50D"  },
  { key: "ret_100d", dd: "dd_100d", rec: "rec_100d", label: "100D" },
  { key: "ret_200d", dd: "dd_200d", rec: "rec_200d", label: "200D" },
  { key: "ret_1y",   dd: "dd_1y",   rec: "rec_1y",   label: "1Y"   },
  { key: "ret_3y",   dd: "dd_3y",   rec: "rec_3y",   label: "3Y"   },
  { key: "ret_5y",   dd: "dd_5y",   rec: "rec_5y",   label: "5Y"   },
];

const fmt = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`);
const cls = (v) => (v == null ? "text-gray-500" : v >= 0 ? "text-green-400" : "text-red-400");
const fmtDd = (v) => (v == null ? "—" : `-${Number(v).toFixed(2)}%`);
const clsDd = (v) => (v == null ? "text-gray-500" : v <= 0.01 ? "text-gray-400" : v >= 10 ? "text-red-400" : "text-orange-400");
const fmtRec = (v) => (v == null ? "—" : v === -1 ? "not yet" : `${v}d`);
const clsRec = (v) => (v == null ? "text-gray-500" : v === -1 ? "text-red-400" : v <= 20 ? "text-green-400" : v <= 60 ? "text-yellow-400" : "text-orange-400");

const HoverBubble = ({ children }) => (
  <div className="pointer-events-none absolute bottom-full left-0 mb-1.5 hidden group-hover:block z-50 w-60 rounded-lg border border-gray-600 bg-gray-900 px-2.5 py-2 text-left shadow-xl">
    {children}
    <div className="absolute top-full left-5 border-4 border-transparent border-t-gray-600" />
  </div>
);

const fmtDate = (d) => {
  if (!d) return "—";
  const [y, m, day] = String(d).split("-");
  return m && day ? `${m}/${day}/${y}` : d;
};

const eventFor = (events, meta) =>
  meta ? (events == null
    ? "Identifying event…"
    : events.events?.[`${meta.peak_date}|${meta.trough_date}`] || "Event unknown") : null;

const returnEventFor = (events, label) =>
  events == null
    ? "Identifying event…"
    : events.return_events?.[label.toLowerCase()] || "Event unknown";

export default function QuickReturnsBar({ ticker }) {
  const { quote: liveQ } = useLiveQuote(ticker);  // SINGLE shared price / 1D source
  const [data, setData] = useState(null);
  const [events, setEvents] = useState(null);
  const [ind, setInd] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Bump `nonce` to force a fresh network request even when the ticker
  // hasn't changed (the "Refresh" button uses this).
  const [nonce, setNonce] = useState(0);
  // Track whether we've already auto-retried for the current ticker/nonce
  // so we don't hammer Yahoo in a loop.
  const autoRetriedRef = useRef(false);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(
        `${API}/stock/quick-returns/${encodeURIComponent(ticker)}`,
        { timeout: 15000, signal }
      );
      if (!signal?.aborted) setData(r.data);
    } catch (e) {
      if (signal?.aborted || axios.isCancel(e)) return;
      setData(null);
      setError(e?.response?.data?.detail || e?.message || "Failed to load returns");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [ticker]);

  useEffect(() => {
    if (!ticker) return;
    autoRetriedRef.current = false;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [ticker, nonce, load]);

  // Lazily fetch AI event labels for each dip once the bar data is in.
  useEffect(() => {
    if (!ticker || !data) return undefined;
    let cancelled = false;
    setEvents(null);
    axios
      .get(`${API}/stock/dip-events/${encodeURIComponent(ticker)}`, { timeout: 45000 })
      .then((r) => { if (!cancelled) setEvents(r.data || {}); })
      .catch(() => { if (!cancelled) setEvents({}); });
    return () => { cancelled = true; };
  }, [ticker, data]);

  // MFI / RSI / VWAP / MACD indicator table — same view as the Overview
  // page's Weighted Market rows, per-ticker.
  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    setInd(null);
    axios
      .get(`${API}/stock/indicator-returns/${encodeURIComponent(ticker)}`, { timeout: 45000 })
      .then((r) => { if (!cancelled && r.data?.rows?.length) setInd(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ticker, nonce]);

  // Silent one-shot auto-retry ~4 s after a transient error, so brief
  // Yahoo blips don't force the user to click Refresh.
  useEffect(() => {
    if (!error || autoRetriedRef.current) return undefined;
    autoRetriedRef.current = true;
    const id = setTimeout(() => setNonce((n) => n + 1), 4000);
    return () => clearTimeout(id);
  }, [error]);

  const onRefresh = useCallback(() => {
    autoRetriedRef.current = false;
    setNonce((n) => n + 1);
  }, []);

  if (!ticker) return null;

  return (
    <>
    <div
      className="bg-[#111] border border-gray-800 rounded-lg px-3 py-2 mb-3 flex items-center gap-2 flex-wrap"
      data-testid="quick-returns-bar"
    >
      <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mr-1">
        Returns
      </span>
      <span className="font-mono text-[11px] text-cyan-300 font-bold">{ticker}</span>
      {(liveQ?.price ?? data?.price) != null && (
        <span className="font-mono text-[11px] text-white" data-testid="qrb-price">${(liveQ?.price ?? data.price).toFixed(2)}</span>
      )}
      <LiveSourceLabel quote={liveQ} testId="qrb-price-source" />
      {loading && <RefreshCw className="w-3 h-3 animate-spin text-purple-400" data-testid="qrb-loading" />}
      {error && !loading && (
        <span
          className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-900/40 border border-amber-700 text-amber-200"
          title={error}
          data-testid="qrb-error"
        >
          <AlertTriangle className="w-3 h-3" />
          Load failed
        </span>
      )}
      <button
        type="button"
        onClick={onRefresh}
        disabled={loading}
        className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 disabled:opacity-40 disabled:cursor-not-allowed"
        title="Refresh timeframe returns"
        data-testid="qrb-refresh"
      >
        <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
        Refresh
      </button>
      <div className="flex items-center gap-3 flex-wrap w-full mt-1">
        {WINDOWS.map((w, idx) => {
          const v = w.key === "ret_1d" && liveQ?.change_1d_pct != null ? liveQ.change_1d_pct : data?.[w.key];
          const rmeta = data?.[`retm_${w.label.toLowerCase()}`];
          return (
            <div
              key={w.key}
              className="relative group flex flex-col items-center min-w-[52px] cursor-help"
              data-testid={`qrb-${w.key}`}
            >
              {rmeta && v != null && (
                <HoverBubble>
                  <p className={`text-[10px] font-bold mb-0.5 ${v >= 0 ? "text-green-300" : "text-red-300"}`}>
                    {w.label} return: {v >= 0 ? "+" : ""}{Number(v).toFixed(2)}%
                  </p>
                  <p className="text-[10px] text-gray-300 font-mono">
                    {fmtDate(rmeta.start_date)} → {fmtDate(rmeta.end_date)}
                  </p>
                  <p className="text-[10px] text-amber-200 mt-1">{returnEventFor(events, w.label)}</p>
                </HoverBubble>
              )}
              <span className="text-[9px] uppercase tracking-wider text-gray-500">{w.label}</span>
              <span className={`text-[12px] font-mono font-bold ${cls(v)}`}>
                {fmt(v)}
              </span>
            </div>
          );
        })}
      </div>
      {/* Indicator returns table — MFI / RSI / VWAP / MACD over the same
          windows, styled like the Overview US Index Returns rows */}
      {ind?.rows?.length > 0 && (
        <div className="w-full mt-2 pt-1.5 border-t border-gray-800 overflow-x-auto" data-testid="stock-indicator-returns">
          <table className="w-full text-xs min-w-[880px]">
            <thead>
              <tr className="text-gray-500 border-b border-gray-800">
                <th className="text-left py-1 px-2 font-medium">Indicator</th>
                {ind.windows.map(w => (
                  <th key={w} className="text-center py-1 px-2 font-medium">{w}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ind.rows.map(row => (
                <tr key={row.key} className="border-b border-gray-800/50 hover:bg-[#161628]">
                  <td className="py-1 px-2 whitespace-nowrap">
                    <span className="text-purple-300 font-semibold">{row.name}</span>
                    {row.current != null && (
                      <span className="text-gray-500 ml-1.5">{Number(row.current).toLocaleString()}</span>
                    )}
                    {(() => {
                      const b = wmCurrentBadge(row);
                      return b ? (
                        <span className={`ml-1.5 px-1.5 py-0.5 rounded border text-[8px] font-bold align-middle ${b.cls}`}
                              data-testid={`sir-extreme-badge-${row.key}`}>{b.text}</span>
                      ) : null;
                    })()}
                    <span className="text-gray-600 ml-1.5 text-[9px]">{row.note}</span>
                  </td>
                  {ind.windows.map(w => {
                    const v = row.values?.[w];
                    const ex = wmCellExtreme(row, w);
                    return (
                      <td key={w} className="text-center py-1 px-2" data-testid={`sir-${row.key}-${w}`}
                          title={ex ? `${row.name} level ${w} ago: ${ex.lvl} (${ex.label})` : undefined}>
                        {v == null ? (
                          <span className="text-gray-600">—</span>
                        ) : (
                          <span className={`font-medium px-1.5 py-0.5 rounded ${v >= 0 ? 'text-green-400' : 'text-red-400'} ${ex ? ex.cls : ''}`}
                                data-extreme={ex ? ex.label : undefined}>
                            {v >= 0 ? '+' : ''}{Number(v).toFixed(2)}%
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    {/* Max Draw Down — worst peak-to-trough decline within each trailing window */}
    <div
      className="bg-[#111] border border-gray-800 rounded-lg px-3 py-2 mb-3 flex items-center gap-2 flex-wrap"
      data-testid="max-drawdown-bar"
    >
      <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mr-1">
        Max Draw Down
      </span>
      <span className="font-mono text-[11px] text-cyan-300 font-bold">{ticker}</span>
      <span className="text-[9px] text-gray-600">worst dip over any 1D–5Y stretch in the last 5 years</span>
      <div className="flex items-center gap-3 flex-wrap w-full mt-1">
        {WINDOWS.map((w, idx) => {
          const v = data?.[w.dd];
          const meta = data?.[`ddm_${w.label.toLowerCase()}`];
          return (
            <div
              key={w.dd}
              className="relative group flex flex-col items-center min-w-[52px] cursor-help"
              data-testid={`mdd-${w.dd}`}
            >
              {meta && v != null && (
                <HoverBubble>
                  <p className="text-[10px] font-bold text-red-300 mb-0.5">
                    Worst {w.label} dip: -{Number(v).toFixed(2)}%
                  </p>
                  <p className="text-[10px] text-gray-300 font-mono">
                    Peak {fmtDate(meta.peak_date)} → Trough {fmtDate(meta.trough_date)}
                  </p>
                  <p className="text-[10px] text-amber-200 mt-1">{eventFor(events, meta)}</p>
                </HoverBubble>
              )}
              <span className="text-[9px] uppercase tracking-wider text-gray-500">{w.label}</span>
              <span className={`text-[12px] font-mono font-bold ${clsDd(v)}`}>
                {fmtDd(v)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
    {/* Days to Recover — trading days from each worst dip's trough back to its prior peak */}
    <div
      className="bg-[#111] border border-gray-800 rounded-lg px-3 py-2 mb-3 flex items-center gap-2 flex-wrap"
      data-testid="recovery-days-bar"
    >
      <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mr-1">
        Days to Recover
      </span>
      <span className="font-mono text-[11px] text-cyan-300 font-bold">{ticker}</span>
      <span className="text-[9px] text-gray-600">trading days from each worst dip's trough back to its prior peak</span>
      <div className="flex items-center gap-3 flex-wrap w-full mt-1">
        {WINDOWS.map((w, idx) => {
          const v = data?.[w.rec];
          const meta = data?.[`ddm_${w.label.toLowerCase()}`];
          return (
            <div
              key={w.rec}
              className="relative group flex flex-col items-center min-w-[52px] cursor-help"
              data-testid={`rec-${w.rec}`}
            >
              {meta && v != null && (
                <HoverBubble>
                  <p className="text-[10px] font-bold text-green-300 mb-0.5">
                    {v === -1
                      ? `Not yet recovered from the worst ${w.label} dip`
                      : `Recovered in ${v} trading day${v === 1 ? "" : "s"}`}
                  </p>
                  <p className="text-[10px] text-gray-300 font-mono">
                    Trough {fmtDate(meta.trough_date)}
                    {meta.recovery_date ? ` → Recovered ${fmtDate(meta.recovery_date)}` : " → still below prior peak"}
                  </p>
                  <p className="text-[10px] text-amber-200 mt-1">{eventFor(events, meta)}</p>
                </HoverBubble>
              )}
              <span className="text-[9px] uppercase tracking-wider text-gray-500">{w.label}</span>
              <span className={`text-[12px] font-mono font-bold ${clsRec(v)}`}>
                {fmtRec(v)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
    </>
  );
}
